import { find } from "lodash";
import moment from "moment";

import { QuestionType } from "@busbud/int-schemas";
import type { MappedOperator } from "@busbud/int-schemas/udm";

import { CHECKED_INPUT_VALUE } from "@app/constants/checkout";
import { FORM_FIELD_KEY } from "@app/modules/checkout/utils/form-fields.constants";
import type { GeoDataService } from "@app/services/geo-data-service";
import type { TranslatorService } from "@app/services/translator";
import type {
  CheckoutFormField,
  CheckoutFormFieldKey,
  CheckoutFormFieldType,
  CheckoutInputState,
  CheckoutPassengerState,
  CheckoutPrimaryContactInfoState
} from "@app/types/checkout-form-types";
import type {
  BillingInfo,
  PassengerCartInfo,
  PassengerCartInfoPassenger,
  PrimaryContactInfo,
  SeatSelectionStateValue
} from "@app/types/checkout-types";
import type { Features } from "@app/types/experiments";
import type { NapiDepartureCart } from "@app/types/napi-departures-types";
import type {
  NapiUser,
  QuestionRestriction,
  TravellerBasicInfo
} from "@app/types/napi-types";
import type { Purchaser } from "@app/types/napi-v2";
import type { NapiV2TripItem } from "@app/types/napi-v2-cart-types";
import type {
  NapiV2Passenger,
  PassengerPayload
} from "@app/types/napi-v2-passengers-types";
import type {
  LocalizedPassengerQuestionChoice,
  PassengerCategoryQuestionCondition,
  PassengerQuestion,
  PassengerQuestionsDependencyGraph
} from "@app/types/passsenger-questions-types";
import { isRecordObject } from "@app/utils/is-record";

import type { CreatePrimaryContactInfoFieldsOptions } from "./forms/primary-contact";
import type {
  ValidationError,
  Validator
} from "../../../helpers/input-validator";
import type { SavedTravellersSelection } from "../container/v2/get-primary-contact-fields-and-state";

import { seatSelectionHasGenders } from "./bus";
import {
  createBillingInfoFields,
  createPassengerFields,
  createQuestionFields,
  isFieldWithPrices
} from "./form-fields";
import * as IdNumberConfig from "./id-numbers";
import { findDuplicatePassengerNames } from "./passenger";
import {
  isPassengerAgeRangeCondition,
  isPassengerCategoryCondition,
  isQuestionCondition,
  isUsingNewPassengerQuestions
} from "./passenger-questions";
import * as InputValidatorHelper from "../../../helpers/input-validator";
import formatter from "../../../utils/formatter";
import { PASSENGER_QUESTION_TYPE } from "../constants";
import { createPrimaryContactInfoFields } from "./forms/primary-contact";
import { getPassengerSectionStateFilledWithSavedPassengerData } from "../container/v2/passenger-question-answers";

interface RestrictionError {
  type: string;
  message: string | null;
  invalid_passengers?: number[];
}

export function getInitialInputState(
  field: CheckoutFormField,
  initial_value: string | undefined | number | boolean | null
): CheckoutInputState {
  if (typeof initial_value !== "string" && typeof initial_value !== "number") {
    initial_value = "";
  }

  const base_input_state: CheckoutInputState = {
    value: initial_value as string,
    error: null,
    changed: false,
    blurred: false,
    loading: false
  };

  // Run field validation and formatters, but keep `changed` state to false
  const input_state = updateInputState(
    field,
    base_input_state,
    initial_value as string
  );
  input_state.changed = false;

  return input_state;
}

export function updateInputState(
  field: CheckoutFormField,
  input_state: CheckoutInputState,
  value: string,
  comparison_value?: string | undefined
): CheckoutInputState {
  if (field.formatter) {
    value = field.formatter(value);
  }

  let error: ValidationError | null = null;
  if (field.validator) {
    error = field.validator(value, comparison_value);
  }

  input_state = Object.assign({}, input_state);
  input_state.value = value;
  input_state.error = error;
  input_state.changed = true;

  return input_state;
}

/**
 * Apply validator / formatter to the whole passenger section state.
 *
 * @param passenger_section_state
 * @param passenger_section_fields
 */
export function validatePassengerSectionState(
  passenger_section_state: CheckoutPassengerState[],
  passenger_section_fields: Array<Array<CheckoutFormField>>
): CheckoutPassengerState[] {
  return passenger_section_state.map((passenger_state, passenger_index) => {
    const new_passenger_state: CheckoutPassengerState = Object.keys(
      passenger_state
    ).reduce((validated_state, field_key) => {
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const passenger_section_field = passenger_section_fields[
        passenger_index
      ].find(({ key }: { key: CheckoutFormFieldKey }) => key === field_key);

      const state = passenger_state[field_key];

      return {
        ...validated_state,
        [field_key]: {
          ...state,
          error: passenger_section_field?.validator
            ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
              passenger_section_field.validator(state.value)
            : null
        }
      };
    }, {});
    return new_passenger_state;
  });
}

function blurInputState(input_state: CheckoutInputState): CheckoutInputState {
  input_state = Object.assign({}, input_state);
  input_state.blurred = true;

  return input_state;
}

/**
 * Create a list of fields to for the passenger section of the form,
 * based on the number of passengers and questions asked by operator
 *
 * @param translator
 * @param geoData
 * @param cart
 * @param cart.passengers
 * @returns
 */
export function createPassengerSectionFields(
  translator: TranslatorService,
  geoData: GeoDataService,
  cart: {
    passengers: PassengerCartInfoPassenger[];
  }
): Array<Array<CheckoutFormField>> {
  return cart.passengers.map(passenger => {
    return createPassengerFields(translator, geoData, passenger);
  });
}

/**
 * Create the initial state for the passenger section, based on values
 * already stored in the cart (ex: from cookies), return an array of
 * "passenger states".
 *
 * @param translator
 * @param geoData
 * @param cart
 * @param user
 * @param passenger_section_fields
 * @param features
 * @param saved_travellers
 * @param saved_travellers_selection
 * @returns
 */
export function createPassengerSectionState(
  translator: TranslatorService,
  geoData: GeoDataService,
  cart: PassengerCartInfo["value"],
  user: NapiUser | undefined | null,
  passenger_section_fields: Array<Array<CheckoutFormField>>,
  features: Partial<Features>,
  saved_travellers: TravellerBasicInfo[],
  saved_travellers_selection: SavedTravellersSelection
): CheckoutPassengerState[] {
  let passenger_section_state = passenger_section_fields.map(
    createPassengerState.bind(null, cart, user, features)
  );

  for (const passenger_index in saved_travellers_selection) {
    const selected_saved_traveller = saved_travellers.find(
      ({ id }) => id === saved_travellers_selection[passenger_index]
    );

    if (selected_saved_traveller) {
      passenger_section_state =
        getPassengerSectionStateFilledWithSavedPassengerData(
          Number(passenger_index),
          selected_saved_traveller,
          // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          passenger_section_fields[Number(passenger_index)],
          passenger_section_state,
          cart,
          features
        );
    }
  }

  if (isUsingNewPassengerQuestions(cart)) {
    return passenger_section_state;
  }

  passenger_section_state = validateAllRestrictions(
    translator,
    geoData,
    cart,
    passenger_section_state
  );

  return passenger_section_state;
}

/**
 * Mapping function to determine the visibility of a passenger question on initial state if it has conditions.
 *
 * @param condition
 * @param passenger
 * @param initial_value
 */
function validateQuestionCondition(
  condition: PassengerCategoryQuestionCondition,
  passenger: PassengerCartInfoPassenger,
  initial_value?: string
): boolean {
  if (isQuestionCondition(condition)) {
    return initial_value === condition.answer;
  } else if (isPassengerCategoryCondition(condition)) {
    // As of today only Amstar uses this condition, but Amstar is disabled. This is a placeholder for future use or a note to safely remove if possible.
    return condition.passenger_categories.includes(
      passenger.passenger_category
    );
  } else if (isPassengerAgeRangeCondition(condition)) {
    return !!(
      typeof passenger.age === "number" &&
      passenger.age >= condition.age_range[0] &&
      passenger.age <= condition.age_range[1]
    );
  }

  // Malformed condition, ask the question anyway
  return true;
}

function createPassengerState(
  cart: PassengerCartInfo["value"],
  user: NapiUser | undefined | null,
  features: Partial<Features>,
  passenger_fields: Array<CheckoutFormField>,
  passenger_index: number
): CheckoutPassengerState {
  return passenger_fields.reduce<CheckoutPassengerState>(
    (passenger_state, field) => {
      let initial_value: string | null | number | undefined | boolean;
      const passenger = cart.passengers[passenger_index];

      let base_initial_value: unknown;
      // FIXME Interconnection: Must adapt Booking's format of questions which are not on the passengers but the booking directly
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      if (typeof passenger.questions === "undefined") {
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        base_initial_value = passenger[field.key as keyof typeof passenger];
      } else {
        base_initial_value =
          // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          passenger[field.key as keyof typeof passenger] ||
          // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          passenger.questions[field.key];
      }

      initial_value = base_initial_value as typeof initial_value;

      const question_key = field.key.replace("passenger_questions.", "");

      if (field.key.indexOf("passenger_questions.") > -1) {
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        initial_value = passenger.questions[question_key];
      }

      // The dob is stored on the cart with `YYYY-MM-DD` as format
      // but the form expects `DD / MM / YYYY`.
      if (
        question_key === FORM_FIELD_KEY.DOB &&
        initial_value &&
        typeof initial_value === "string" &&
        initial_value.match(/\d{4}-\d{2}-\d{2}/)
      ) {
        initial_value = moment(initial_value, "YYYY-MM-DD").format(
          "DD / MM / YYYY"
        );
      }

      if (
        !initial_value &&
        question_key === FORM_FIELD_KEY.ID_TYPE &&
        features.AUTO_SELECT_ID_TYPE
      ) {
        initial_value = getValueOfFirstOption(
          features.AUTO_SELECT_ID_TYPE,
          field
        );
      }

      if (question_key === FORM_FIELD_KEY.RESIDENCY) {
        // avoid pre-selecting the first value of the countries list
        initial_value = "";
      }

      if (!initial_value && question_key === FORM_FIELD_KEY.DISCOUNT) {
        initial_value = "none";
      }

      const is_primary_passenger = passenger_index === 0;
      if (field.key === FORM_FIELD_KEY.EMAIL && user && is_primary_passenger) {
        initial_value = user.email;
      }

      // Value from cookies might be different than offered options
      if (
        field.type === "select" &&
        field.options &&
        !field.options.some(({ value }) => value === initial_value)
      ) {
        initial_value = undefined;
      }

      // for loyalty program, we need to set a default value to none
      if (
        !initial_value &&
        question_key === FORM_FIELD_KEY.OPERATOR_LOYALTY_PROGRAM
      ) {
        // Safe guard to ensure none is actually an option before setting it as the default value
        const is_none_an_option = field.options?.some(
          option => option.value === "none"
        );
        if (is_none_an_option) {
          initial_value = "none";
        }
      }

      if (field.type === "checkbox" && typeof initial_value === "boolean") {
        // Boolean passenger questions state are held in the cart as boolean values but the DOM API does not know how to deal with primitive boolean values,it needs strings("true" or "false").
        // Because of this, we need to convert the boolean values from the cart to a string values for the form state that way when the form state is
        // bind to a checkbox input, the DOM API is be able to render the element in its correct state
        initial_value = initial_value ? "true" : "false";
      }

      if (!initial_value) {
        const filtered_options: CheckoutFormField["options"] = [];
        if (field.options) {
          field.options.forEach(option => {
            let visible = true;
            if (option.conditions) {
              if (option.conditions.all) {
                visible = option.conditions.all.every(
                  (condition: PassengerCategoryQuestionCondition) =>
                    validateQuestionCondition(
                      condition,
                      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                      passenger,
                      initial_value as string | undefined
                    )
                );
              } else if (option.conditions.any) {
                visible = option.conditions.any.some(
                  (condition: PassengerCategoryQuestionCondition) =>
                    validateQuestionCondition(
                      condition,
                      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                      passenger,
                      initial_value as string | undefined
                    )
                );
              } else if (option.conditions.none) {
                visible = option.conditions.none.every(
                  (condition: PassengerCategoryQuestionCondition) =>
                    !validateQuestionCondition(
                      condition,
                      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                      passenger,
                      initial_value as string | undefined
                    )
                );
              }
            }
            if (!visible) {
              return;
            }
            filtered_options.push(option as LocalizedPassengerQuestionChoice);
          });
        }
        // Automatically fill the dropdown IF there is only single option.
        if (
          field.type === "select" &&
          filtered_options &&
          filtered_options.length === 1
        ) {
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          initial_value = filtered_options[0].value;
        }
        // Automatically select the first value for price modifying passenger questions.
        if (
          field.type === "select" &&
          isFieldWithPrices(field.key) &&
          filtered_options
        ) {
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          initial_value = filtered_options[0].value;
        }
      }

      passenger_state[field.key] = getInitialInputState(field, initial_value);

      return passenger_state;
    },
    {}
  );
}

function validateAllRestrictions(
  translator: TranslatorService,
  geoData: GeoDataService,
  cart: PassengerCartInfo["value"],
  passenger_section_state: CheckoutPassengerState[]
): CheckoutPassengerState[] {
  if (!cart.questions) {
    return passenger_section_state;
  }

  return createQuestionFields(translator, geoData).reduce(
    (state, field, index) => {
      state = validateRestrictionsForField(cart, state, index, field);
      state = validateDuplicateNames(state);
      return state;
    },
    passenger_section_state
  );
}

function getFieldRestrictions(
  cart: PassengerCartInfo["value"],
  field_key: CheckoutFormFieldKey
): Array<QuestionRestriction> | undefined {
  if (!cart.questions || !cart.questions[field_key]) {
    return;
  }
  const question = cart.questions[field_key];
  return "restrictions" in question && Array.isArray(question.restrictions)
    ? (question.restrictions as Array<QuestionRestriction>)
    : undefined;
}

export function getValueOfFirstOption(
  values: string[],
  field: CheckoutFormField
): string | undefined {
  if (!values.length || !field.options) {
    return;
  }
  const options = field.options || [];
  return values.find(value => !!options.find(option => option.value === value));
}

export function getFieldType(
  question: PassengerQuestion
): CheckoutFormFieldType {
  switch (question.question_type) {
    case PASSENGER_QUESTION_TYPE.SELECTION:
      return "select";
    case PASSENGER_QUESTION_TYPE.BOOLEAN:
      return "checkbox";
    default:
      return "text";
  }
}

export function updatePassengerFields(
  translator: TranslatorService,
  passenger_section_fields: Array<Array<CheckoutFormField>>,
  cart: PassengerCartInfo["value"],
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number
): Array<Array<CheckoutFormField>> {
  const passenger_fields = passenger_section_fields[passenger_index];
  if (!passenger_fields) {
    throw new Error(
      `Expected to find fields for passenger '${passenger_index}'`
    );
  }
  const passenger_state = passenger_section_state[passenger_index];
  if (!passenger_state) {
    throw new Error(
      `Expected to find state for passenger '${passenger_index}'`
    );
  }
  const passenger = cart.passengers[passenger_index];

  const new_passenger_fields = passenger_fields.map<CheckoutFormField>(
    (field): CheckoutFormField => {
      if (field.key.indexOf("passenger_questions.") === -1) {
        return field;
      }

      const question_key = field.key.replace("passenger_questions.", "");
      const question = cart.passenger_questions.find(
        q => q.key === question_key
      );
      if (!question) {
        throw new Error(
          `Expected to find question definition for '${question_key}'`
        );
      }

      /**
       * Mapper function to re-validate the condition of a question based on an updated value.
       *
       * @param condition
       */
      function revalidateQuestionCondition(
        condition: PassengerCategoryQuestionCondition
      ) {
        if (isQuestionCondition(condition)) {
          return validateQuestionCondition(
            condition,
            // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            passenger,
            // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            passenger_state[`passenger_questions.${condition.question}`].value
          );
        }
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        return validateQuestionCondition(condition, passenger);
      }

      let visible = true;
      if (question.conditions) {
        if (question.conditions.all) {
          visible = question.conditions.all.every(revalidateQuestionCondition);
        } else if (question.conditions.any) {
          visible = question.conditions.any.some(revalidateQuestionCondition);
        } else if (question.conditions.none) {
          visible = !question.conditions.none.every(
            revalidateQuestionCondition
          );
        }
      }
      if (!visible) {
        return {
          key: field.key,
          type: "hidden"
        };
      }

      field = {
        key: field.key,
        type: getFieldType(question),
        label: question.label,
        help_text: question.help_text
      };

      if (
        "tooltip" in question &&
        question.tooltip &&
        isRecordObject(question.tooltip) &&
        "label" in question.tooltip &&
        typeof question.tooltip.label === "string"
      ) {
        field.tooltip = question.tooltip.label;
      }

      if (question.question_type === PASSENGER_QUESTION_TYPE.SELECTION) {
        const options: CheckoutFormField["options"] = [];
        question.choices?.forEach(choice => {
          let visible = true;
          if (choice.conditions) {
            if (choice.conditions.all) {
              visible = choice.conditions.all.every(
                revalidateQuestionCondition
              );
            } else if (choice.conditions.any) {
              visible = choice.conditions.any.some(revalidateQuestionCondition);
            } else if (choice.conditions.none) {
              visible = !choice.conditions.none.every(
                revalidateQuestionCondition
              );
            }
          }
          if (!visible) {
            return;
          }

          options?.push(choice as LocalizedPassengerQuestionChoice);
        });

        field.readOnly = options.length === 1;
        field.options = options;
      }

      // TODO(questions): running this on each input change,
      // see if we can put somewhere else for better performance
      const validators: Validator[] = [];

      // If a boolean question is required, this will means it has to be checked by the user. If we want to enforce a given value(false or true) we should do it by using a validation attribute like for questions of type text
      if (
        question.question_type !== QuestionType.Boolean &&
        !question.is_optional
      ) {
        validators.push(InputValidatorHelper.required());
      }

      if (
        "validations" in question &&
        question.validations &&
        question.validations.length > 0
      ) {
        // TODO(questions): for some reason validations don't re-run properly if
        // I change the value of the `id_type` dropdown
        question.validations.forEach(validation => {
          let enabled = true;
          if (validation.conditions) {
            if (validation.conditions.all) {
              enabled = validation.conditions.all.every(condition => {
                return (
                  "question" in condition &&
                  "answer" in condition &&
                  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                  passenger_state[`passenger_questions.${condition.question}`]
                    .value === condition.answer
                );
              });
            } else if (validation.conditions.any) {
              enabled = validation.conditions.any.some(condition => {
                return (
                  "question" in condition &&
                  "answer" in condition &&
                  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                  passenger_state[`passenger_questions.${condition.question}`]
                    .value === condition.answer
                );
              });
            }
          }
          if (!enabled) {
            return;
          }

          /** @type {string} TODO(questions): format_helper as part of API response */
          let format_helper = "";

          let validator: Validator | undefined;
          let message = translator.t(
            "!checkout.passenger-questions.validation-regex.error"
          );

          const updateMessageAndPlaceholder = () => {
            message = format_helper
              ? [
                  message,
                  translator.t(
                    "!checkout.passenger-questions.validation-regex.error-example",
                    {
                      format_helper
                    }
                  )
                ].join(" ")
              : message;

            field.placeholder = format_helper
              ? translator.t(
                  "!checkout.passenger-questions.validation-regex.placeholder",
                  {
                    format_helper
                  }
                )
              : undefined;
          };

          if (question_key === FORM_FIELD_KEY.ID_NUMBER) {
            format_helper = "26.123.410";
            const passenger_id_type = passenger_state[
              "passenger_questions.id_type"
            ]
              ? passenger_state["passenger_questions.id_type"].value
              : null;

            // Some id_numbers like Turkish have a custom config
            const id_number_config = passenger_id_type
              ? IdNumberConfig.getByIdType(passenger_id_type)
              : null;

            if (id_number_config) {
              format_helper = id_number_config.hasOwnProperty("placeholder")
                ? id_number_config.placeholder || ""
                : format_helper;
              updateMessageAndPlaceholder();
              validator = id_number_config.validator(message);
            }
          }

          if (!validator && validation.regex) {
            if (question_key === FORM_FIELD_KEY.CUIL_ARG) {
              format_helper = "24857109827";
              updateMessageAndPlaceholder();
            }

            validator = InputValidatorHelper.regExp(
              new RegExp(validation.regex),
              message
            );
          }

          if (validator) {
            validators.push(validator);
          }
        });
      }

      field.validator = InputValidatorHelper.combineValidators(validators);

      if (question.question_type === PASSENGER_QUESTION_TYPE.DATE) {
        field.placeholder = translator.t(
          "!checkout.passengers.date.placeholder"
        );
        field.input_format_hint = translator.t(
          "!checkout.passengers.date.placeholder"
        );
        field.formatter = function (value) {
          return formatter.passengerFormDate(value) || "";
        };
        field.serializer = function (value) {
          const serialized = formatter.passengerFormDateToDate(value);

          if (serialized instanceof Error) {
            return value;
          }

          return serialized;
        };

        const validators = [InputValidatorHelper.required()];
        if (question_key === FORM_FIELD_KEY.DOB) {
          validators.push(InputValidatorHelper.dateOfBirth());
        } else if (question_key === FORM_FIELD_KEY.ID_EXPIRY_DATE) {
          validators.push(InputValidatorHelper.idExpiryDate());
        }
        field.validator = InputValidatorHelper.combineValidators(validators);
      } else if (question.question_type === QuestionType.Boolean) {
        field.serializer = function (value) {
          if (typeof value === "boolean") {
            return value;
          }

          return value === CHECKED_INPUT_VALUE;
        };
      }

      if ("enforce_unique" in question && question.enforce_unique) {
        field.restrictions = {
          unique: translator.t(
            "!checkout.passenger-questions.enforce-unique.error",
            { question_label: question.label }
          )
        };
      }

      return field;
    }
  );

  const new_passenger_section_fields = passenger_section_fields.map<
    Array<CheckoutFormField>
  >((passenger_fields, index): Array<CheckoutFormField> => {
    if (index !== passenger_index) {
      return passenger_fields;
    }
    return new_passenger_fields;
  });

  return new_passenger_section_fields;
}

export function updatePassengerSectionState(
  cart: PassengerCartInfo["value"],
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number,
  field: CheckoutFormField,
  value: string
): CheckoutPassengerState[] {
  // First, update the passenger independently for the provided field
  let new_passenger_section_state = updateInputStateForPassenger(
    passenger_section_state,
    passenger_index,
    field,
    input_state => {
      return updateInputState(field, input_state, value);
    }
  );

  // Second, validate restrictions taking into account all passengers for that field
  new_passenger_section_state = validateRestrictionsForField(
    cart,
    new_passenger_section_state,
    passenger_index,
    field
  );

  // Last, validate the names are not duplicated
  passenger_section_state = validateDuplicateNames(passenger_section_state);

  return new_passenger_section_state;
}

export function updatePassengerQuestionState(
  cart: PassengerCartInfo["value"],
  passenger_section_state: CheckoutPassengerState[],
  passenger_section_fields: Array<Array<CheckoutFormField>>,
  questions_dependency_graph: PassengerQuestionsDependencyGraph,
  passenger_index: number,
  field: CheckoutFormField,
  value: string
): CheckoutPassengerState[] {
  const passenger_fields = passenger_section_fields[passenger_index];
  if (!passenger_fields) {
    throw new Error(
      `Expected to find fields for passenger '${passenger_index}'`
    );
  }
  const passenger_state = passenger_section_state[passenger_index];
  if (!passenger_state) {
    throw new Error(
      `Expected to find state for passenger '${passenger_index}'`
    );
  }

  // First, update the passenger independently for the provided field
  let new_passenger_section_state = updateInputStateForPassenger(
    passenger_section_state,
    passenger_index,
    field,
    input_state => {
      return updateInputState(field, input_state, value);
    }
  );

  // Second, update all other question inputs whose validations or choices may have changed
  const question_key = field.key.replace("passenger_questions.", "");
  const graph_entry = questions_dependency_graph[question_key];
  if (graph_entry) {
    passenger_fields.forEach(other_field => {
      // Question that was just changed by user, we already updated this one
      if (other_field.key === field.key) {
        return;
      }

      // Only passenger questions are dependent on each other, not other passenger information
      if (other_field.key.indexOf("passenger_questions.") === -1) {
        return;
      }

      // Only update this question if it is dependent on the question whose value just changed
      const other_question_key = other_field.key.replace(
        "passenger_questions.",
        ""
      );
      if (!graph_entry.successors[other_question_key]) {
        return;
      }

      new_passenger_section_state = updateInputStateForPassenger(
        new_passenger_section_state,
        passenger_index,
        other_field,
        input_state => {
          // Reset value as choices or validations have likely changed
          let value = "";
          if (
            other_field.type === "select" &&
            other_field.options &&
            other_field.options.length === 1
          ) {
            // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            value = other_field.options[0].value;
          }
          return updateInputState(other_field, input_state, value);
        }
      );
    });
  }

  // Third, validate restrictions taking into account all passengers for that field
  // Simulate old-schema restrictions on cart object to re-use same functionality
  const cart_with_restrictions = Object.assign({}, cart, {
    questions: cart.passenger_questions.reduce<Record<string, unknown>>(
      (acc, question) => {
        if ("enforce_unique" in question && question.enforce_unique) {
          acc[`passenger_questions.${question.key}`] = {
            restrictions: ["unique"]
          };
        }
        return acc;
      },
      {}
    )
  });
  new_passenger_section_state = validateRestrictionsForField(
    cart_with_restrictions,
    new_passenger_section_state,
    passenger_index,
    field
  );

  new_passenger_section_state = validateDuplicateNames(
    new_passenger_section_state
  );

  return new_passenger_section_state;
}

function updateInputStateForPassenger(
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number,
  field: CheckoutFormField,
  callback: (input_state: CheckoutInputState) => CheckoutInputState
): CheckoutPassengerState[] {
  const passenger_state = passenger_section_state[passenger_index];
  if (!passenger_state) {
    throw new Error(
      `Expected to find state for passenger '${passenger_index}'`
    );
  }

  let input_state = passenger_state[field.key];
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  input_state = callback(input_state);

  const new_passenger_state = Object.assign({}, passenger_state, {
    [field.key]: input_state
  });

  const new_passenger_section_state = passenger_section_state.map(
    (passenger_state, index) => {
      if (index !== passenger_index) {
        return passenger_state;
      }
      return new_passenger_state;
    }
  );

  return new_passenger_section_state;
}

/**
 * Check any restrictions (defined on the `cart`) for a question field
 * Restrictions (ex: "unique") need to be checked against all values of that
 * field (i.e. for every passenger)
 *
 * @param cart
 * @param passenger_section_state
 * @param passenger_index
 * @param field
 * @returns
 */
export function validateRestrictionsForField(
  cart: PassengerCartInfo["value"],
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number,
  field: CheckoutFormField
): CheckoutPassengerState[] {
  const restrictions = getFieldRestrictions(cart, field.key);
  if (!restrictions) {
    return passenger_section_state;
  }

  // Clear any previous restriction errors by re-validating all inputs for that field
  passenger_section_state = updateAllPassengersForField(
    passenger_section_state,
    passenger_index,
    field.key,
    input_state => {
      return Object.assign({}, input_state, {
        error:
          field.validator && input_state
            ? field.validator(input_state.value)
            : null
      });
    }
  );

  // Gather each passenger's value for the field in an array
  const all_passenger_values = passenger_section_state.map(passenger_state => {
    if (!passenger_state.hasOwnProperty(field.key)) {
      return "";
    }
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    return passenger_state[field.key].value;
  });

  // Find the first restriction failing for that field
  let restriction_error: RestrictionError | null = null;

  restrictions.forEach(restriction => {
    if (!restriction_error) {
      // Get the error message to display under the input, if any
      const error_message =
        (field.restrictions && field.restrictions[restriction]) || null;

      if (restriction === "unique") {
        restriction_error = validateUniqueRestriction(
          all_passenger_values,
          error_message
        );
      }
    }
  });

  if (!restriction_error) {
    return passenger_section_state;
  }

  // If a restriction didn't pass, add the error to each passenger's input
  // state for that particular field
  passenger_section_state = addRestrictionErrorForField(
    passenger_section_state,
    passenger_index,
    field.key,
    restriction_error
  );

  return passenger_section_state;
}

/**
 * Check wether passengers share the same name and
 * assign errors on first_name and last_name fields if needed
 *
 * @param passenger_section_state
 * @returns
 */
export function validateDuplicateNames(
  passenger_section_state: CheckoutPassengerState[]
): CheckoutPassengerState[] {
  const duplicates = findDuplicatePassengerNames(passenger_section_state);
  const new_state = passenger_section_state.map<CheckoutPassengerState>(
    (passenger_state, idx): CheckoutPassengerState => {
      const error: ValidationError = {
        name: "full_name_not_unique",
        message: null
      };
      const [first_name, last_name] = [
        passenger_state.first_name,
        passenger_state.last_name
      ].map(input => {
        // Field is part of a duplicate,
        // Mark it as errored
        if (duplicates[idx]) {
          return {
            ...input,
            blurred: true,
            changed: true,
            error: { ...error }
          };
        }
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        if (!input.error || input.error.name !== "full_name_not_unique") {
          return input;
        }

        // Remove the original error
        return {
          ...input,
          blurred: true,
          changed: true,
          error: null
        };
      });
      return {
        ...passenger_state,
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        first_name,
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        last_name
      };
    }
  );
  return new_state;
}

function validateUniqueRestriction(
  all_passenger_values: string[],
  error_message: string | null
): RestrictionError | null {
  let invalid_passengers: number[] = [];

  invalid_passengers = all_passenger_values.reduce(
    (invalid_passengers, value, passenger_index) => {
      if (value === null || !value.length) {
        // Don't check null and empty strings for uniqueness
        return invalid_passengers;
      }

      const is_duplicate = all_passenger_values.reduce(
        (is_duplicate, other_value, other_passenger_index) => {
          if (is_duplicate) {
            return is_duplicate;
          }

          if (other_passenger_index === passenger_index) {
            return false;
          }

          return other_value === value;
        },
        false
      );

      if (is_duplicate) {
        invalid_passengers.push(passenger_index);
      }

      return invalid_passengers;
    },
    invalid_passengers
  );

  if (invalid_passengers.length > 0) {
    return {
      type: "not_unique",
      message: error_message,
      invalid_passengers: invalid_passengers
    };
  } else {
    return null;
  }
}

function addRestrictionErrorForField(
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number,
  field_key: CheckoutFormFieldKey,
  restriction_error: RestrictionError | undefined
): CheckoutPassengerState[] {
  return updateAllPassengersForField(
    passenger_section_state,
    passenger_index,
    field_key,
    (input_state, index) => {
      // Don't overwrite non-restriction errors with a restriction error
      if (input_state.error) {
        return input_state;
      }

      if (
        restriction_error &&
        restriction_error.invalid_passengers &&
        restriction_error.invalid_passengers.indexOf(index) > -1
      ) {
        return Object.assign({}, input_state, {
          error: {
            name: restriction_error.type,
            message: restriction_error.message
          }
        });
      }

      return input_state;
    }
  );
}

function updateAllPassengersForField(
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number,
  field_key: CheckoutFormFieldKey,
  callback: (
    input_state: CheckoutInputState,
    passenger_index: number
  ) => CheckoutInputState
): CheckoutPassengerState[] {
  return passenger_section_state.map((passenger_state, index) => {
    let input_state = passenger_state[field_key];
    if (typeof input_state === "undefined") {
      return { ...passenger_state };
    }

    if (passenger_index !== index) {
      return { ...passenger_state };
    }

    input_state = callback(input_state, index);

    return {
      ...passenger_state,
      [field_key]: input_state
    };
  });
}

export function blurPassengerSectionInput(
  passenger_section_state: CheckoutPassengerState[],
  passenger_index: number,
  field: CheckoutFormField
): CheckoutPassengerState[] {
  return updateInputStateForPassenger(
    passenger_section_state,
    passenger_index,
    field,
    blurInputState
  );
}

export function blurFormInput(
  form_state: Record<string, CheckoutInputState>,
  field: CheckoutFormField
): Record<string, CheckoutInputState> {
  const input_state = form_state[field.key];

  return Object.assign({}, form_state, {
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    [field.key]: blurInputState(input_state)
  });
}

export function isFormValid(
  form_state: Record<string, CheckoutInputState>
): boolean {
  return Object.values(form_state).every(({ error }) => !error);
}

export function isPassengerSectionValid(
  passenger_section_state: CheckoutPassengerState[]
): boolean {
  return !!passenger_section_state.reduce((is_valid, passenger_state) => {
    if (!is_valid) {
      return false;
    }

    return isFormValid(passenger_state);
  }, true);
}

export function getFormErrors(
  form_state:
    | CheckoutPrimaryContactInfoState
    | CheckoutPassengerState
    | CheckoutPassengerState[]
): string[] {
  const errors: string[] = [];

  for (const field_name in form_state) {
    const value = (form_state as any)[field_name];
    if (value.error) {
      errors.push(field_name);
    }
  }

  return errors;
}

/**
 * Return the `cart.passengers`, merging the new values from the form
 *
 * @param cart
 * @param passenger_section_state
 * @param seat_selection_state_value
 * @param primary_contact_id
 * @param [primary_contact_info_state]
 * @returns
 */
export function getUpdatedPassengers(
  cart: PassengerCartInfo["value"],
  passenger_section_state: CheckoutPassengerState[],
  seat_selection_state_value: SeatSelectionStateValue,
  primary_contact_id: string,
  primary_contact_info_state: CheckoutPrimaryContactInfoState
): PassengerCartInfoPassenger[] {
  const passengers = cart.passengers.map((passenger, passenger_index) => {
    const passenger_state = passenger_section_state[passenger_index];
    const question_keys = isUsingNewPassengerQuestions(cart)
      ? cart.passenger_questions.map(
          (question: { key: string }) => `passenger_questions.${question.key}`
        )
      : Object.keys(passenger.questions);
    let updated_questions =
      typeof passenger.questions === "undefined"
        ? {}
        : question_keys.reduce<
            Record<string, string | null | number | boolean>
          >((questions, key) => {
            // If the question key is unknown, it wasn't rendered on the screen
            // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            if (typeof passenger_state[key] !== "undefined") {
              questions[key] =
                // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                passenger_state[key].value.length ||
                // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                typeof passenger_state[key].value === "number"
                  ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                    passenger_state[key].value
                  : null;
            } else {
              // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
              questions[key] = passenger.questions[key];
            }

            return questions;
          }, {});

    if (isUsingNewPassengerQuestions(cart)) {
      updated_questions = Object.keys(updated_questions).reduce<
        Record<string, string | null | number | boolean>
      >((questions, key) => {
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        questions[key.replace("passenger_questions.", "")] =
          updated_questions[key];
        return questions;
      }, {});
    }
    // TODO Interconnection seat selection
    const departure_ids: { outbound?: string; return?: string } = {};

    // TODO Interconnection seat selection
    Object.keys(seat_selection_state_value).forEach(departure_id => {
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const departure_type = seat_selection_state_value[departure_id].direction;
      if (departure_type === "outbound") {
        departure_ids.outbound = departure_id;
      }
      if (departure_type === "return") {
        departure_ids.return = departure_id;
      }
    });

    const updated_passenger: typeof passenger & {
      outbound_selected_seat?: string | null;
      return_selected_seat?: string | null;
    } = { ...passenger };
    Object.keys(passenger).forEach(key => {
      if (key === "questions") {
        updated_passenger.questions = updated_questions;
      } else if (
        key === "outbound_selected_seat" &&
        departure_ids.outbound // TODO Interconnection seat selection
      ) {
        const seat =
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          seat_selection_state_value[departure_ids.outbound].seats[
            passenger_index
          ];
        updated_passenger.outbound_selected_seat =
          (seat && seat.type !== "empty" && seat.id) || null;
      } else if (
        key === "return_selected_seat" &&
        departure_ids.return // TODO Interconnection seat selection
      ) {
        const seat =
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          seat_selection_state_value[departure_ids.return].seats[
            passenger_index
          ];
        updated_passenger.return_selected_seat =
          (seat && seat.type !== "empty" && seat.id) || null;
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      } else if (typeof passenger_state[key] !== "undefined") {
        (updated_passenger[key as keyof typeof updated_passenger] as any) =
          // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          passenger_state[key].value.length ? passenger_state[key].value : null;
      }
    });

    updated_passenger.primary_contact =
      updated_passenger.id === primary_contact_id;

    if (updated_passenger.primary_contact && primary_contact_info_state) {
      updated_passenger.email = primary_contact_info_state.email.value;
      updated_passenger.phone_number =
        primary_contact_info_state.phone_number?.value;
      updated_passenger.email_opt_in =
        primary_contact_info_state.email_opt_in.value === CHECKED_INPUT_VALUE;
      updated_passenger.send_tickets_to_whatsapp =
        primary_contact_info_state.send_tickets_to_whatsapp.value ===
          CHECKED_INPUT_VALUE || passenger.send_tickets_to_whatsapp;

      if (primary_contact_info_state.address) {
        updated_passenger.address = {
          line_1: primary_contact_info_state.address.value,
          line_2: primary_contact_info_state.address_complement.value,
          city: primary_contact_info_state.city.value,
          region: primary_contact_info_state.state.value,
          postal_code: primary_contact_info_state.zip.value,
          country: primary_contact_info_state.country.value
        };
      }
    }

    return updated_passenger;
  });

  return passengers;
}

export function getUpdatedPurchaser(
  passenger_section_state: CheckoutPassengerState[],
  primary_contact_info_state: CheckoutPrimaryContactInfoState
): Purchaser {
  const primary_contact =
    passenger_section_state.find(
      passenger => passenger.is_primary?.value === "true"
    ) || passenger_section_state[0];
  const { email, phone_number, email_opt_in, send_tickets_to_whatsapp } =
    primary_contact_info_state;

  return {
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    first_name: primary_contact.first_name?.value,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    last_name: primary_contact.last_name?.value,
    email: email?.value,
    phone: phone_number?.value,
    opt_in_marketing: email_opt_in?.value === "true",
    send_tickets_to_whatsapp: send_tickets_to_whatsapp?.value === "true"
  };
}

export function getUpdatedPassengersV2(
  trip: NapiV2TripItem,
  passenger_section_state: CheckoutPassengerState[],
  saved_travellers_selection: SavedTravellersSelection,
  _cart_seat_selected?: NapiV2Passenger["selected_seats"], // TODO-CART-V2-SEAT-SELECTION: it might not be necessary to pass this in
  ignore_errors?: boolean
): PassengerPayload[] {
  const passengers: PassengerPayload[] = trip.passengers.map(
    (passenger, passenger_index) => {
      const passenger_state = passenger_section_state[passenger_index];

      let trip_questions = trip.questions;
      if (ignore_errors) {
        trip_questions = trip_questions.filter(
          // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          ({ key }) => !passenger_state[`passenger_questions.${key}`].error
        );
      }

      const updated_answers = trip_questions.map(({ key: question_key }) => {
        const passenger_state_question =
          // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          passenger_state[`passenger_questions.${question_key}`];

        // If the question key is unknown, it wasn't rendered on the screen
        if (typeof passenger_state_question !== "undefined") {
          const valid_fields: string[] = [
            FORM_FIELD_KEY.DOB,
            FORM_FIELD_KEY.ID_EXPIRY_DATE
          ];
          if (
            valid_fields.includes(question_key) &&
            passenger_state_question.value
          ) {
            return {
              question_key,
              value: formatter.passengerFormDateToDate(
                passenger_state_question.value
              )
            };
          }
          return {
            question_key,
            value: passenger_state_question.value ?? ""
          };
        } else {
          const initial_answer = passenger.answers?.find(
            answer => answer.question_key === question_key
          );
          return initial_answer || { question_key, value: "" };
        }
      });

      const updated_passenger = Object.fromEntries(
        Object.entries(passenger)
          .map(([key, value]) => {
            if (key === "answers") {
              return [key, updated_answers];
            }
            if (key === "selected_seats") {
              return [key, passenger.selected_seats];
            }
            if (key === "address") {
              //TODO passenger address
              return [key, null];
              // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            } else if (passenger_state[key]?.value) {
              // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
              return [key, passenger_state[key].value];
            }
            return [key, value];
          })
          // Optional fields must be omitted from the object sent to the api
          .filter(([_key, value]) => value !== null)
      );

      if (saved_travellers_selection[passenger_index]) {
        updated_passenger.id = saved_travellers_selection[passenger_index];
      }

      return updated_passenger;
    }
  );

  return passengers;
}

/**
 * Serialize some passenger fields before sending to API
 *
 * @param passengers
 * @param passenger_section_fields
 */
export function serializePassengers(
  passengers: Array<PassengerCartInfoPassenger>,
  passenger_section_fields: CheckoutFormField[][]
): Array<PassengerCartInfoPassenger> {
  const question_fields_map = passenger_section_fields.map(passenger_section =>
    passenger_section.reduce<Record<string, CheckoutFormField>>(
      (acc, field) => {
        acc[field.key] = field;
        return acc;
      },
      {}
    )
  );

  return passengers.map((passenger, idx) => {
    const serialized_questions = serializePassengerQuestions(
      passenger.questions,
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      question_fields_map[idx]
    );
    passenger = Object.assign({}, passenger);
    passenger.questions = serialized_questions;
    return passenger;
  });
}

/**
 * Some passenger questions such as boolean questions need to be serialized because of the DOM API storing boolean values as strings.
 * For Boolean questions, this step of serialization is turning the string value into its corresponding boolean value(aka "true" => true)
 *
 * @param questions
 * @param field_map
 */
function serializePassengerQuestions(
  questions: PassengerCartInfoPassenger["questions"],
  field_map: Record<string, CheckoutFormField>
): PassengerCartInfoPassenger["questions"] {
  return Object.keys(questions).reduce<PassengerCartInfoPassenger["questions"]>(
    (serialized_obj, key) => {
      /**
       * At this point, questions object only contains string or number values even if the type suggests that it can holds boolean values.
       * The reason why is that we assign the passenger_state[key].value as value to the passenger.questions[key] but all passenger_state[key].value values are of type string.
       * Because we want to serialize those string values and turn them into a string or a boolean
       * we need to cast them to their actual type(string) to be able to use them as param of the serialize(value: sting) function that might be attached to their corresponding FormField object
       */
      const value = questions[key] as string | number;
      const field = field_map[`passenger_questions.${key}`] || field_map[key];

      const serializer = field?.serializer;

      /** 0 will default to false so we skip check on number types without casting **/
      if (!value && typeof value !== "number") {
        serialized_obj[key] = null;
      } else if (serializer) {
        serialized_obj[key] = serializer(value as string);
      } else {
        serialized_obj[key] = value;
      }

      return serialized_obj;
    },
    {}
  );
}

export { createBillingInfoFields };

export function createBillingInfoState(
  billing_info_fields: Array<CheckoutFormField>,
  restored_billing_info: BillingInfo
): Record<string, CheckoutInputState> {
  return billing_info_fields.reduce<Record<string, CheckoutInputState>>(
    (billing_info_state, field) => {
      const initial_value: string | undefined | null = restored_billing_info
        ? restored_billing_info[field.key as keyof BillingInfo]
        : null;

      billing_info_state[field.key] = getInitialInputState(
        field,
        initial_value
      );

      return billing_info_state;
    },
    {}
  );
}

export function updateBillingInfoFields(
  translator: TranslatorService,
  geoData: GeoDataService,
  billing_info_fields: Array<CheckoutFormField>,
  field: CheckoutFormField,
  value: string
): Array<CheckoutFormField> {
  if (field.key === FORM_FIELD_KEY.COUNTRY) {
    const country_iso3 = value;
    return createBillingInfoFields(translator, geoData, country_iso3);
  }

  return billing_info_fields;
}

export function updatePrimaryContactInfoFields(
  translator: TranslatorService,
  user: NapiUser | undefined | null,
  operators: Array<MappedOperator>,
  mandatory_phone_number: boolean,
  browsing_country_code_2: string,
  geoData: GeoDataService,
  primary_contact_info_fields: Array<CheckoutFormField>,
  field: CheckoutFormField,
  options: CreatePrimaryContactInfoFieldsOptions,
  value: string
): Array<CheckoutFormField> {
  if (field.key === FORM_FIELD_KEY.COUNTRY) {
    const country_iso3 = value;
    return createPrimaryContactInfoFields(
      translator,
      user,
      operators,
      mandatory_phone_number,
      browsing_country_code_2,
      geoData,
      country_iso3,
      options
    );
  }

  return primary_contact_info_fields;
}

export function findOrThrow<T>(arr: readonly T[], predicate: Partial<T>): T {
  const result = find(arr, predicate);
  if (result === undefined) {
    throw new Error(`A value for ${predicate} must exist in the array ${arr}`);
  }
  return result as T;
}

export function updateBillingInfoState(
  geoData: GeoDataService,
  billing_info_fields: Array<CheckoutFormField>,
  billing_info_state: Record<string, CheckoutInputState>,
  field: CheckoutFormField,
  value: string
): Record<string, CheckoutInputState> {
  const input_state = billing_info_state[field.key];
  const state_field: CheckoutFormField = findOrThrow(billing_info_fields, {
    key: "state"
  });
  const state_state = billing_info_state.state;

  if (field.key === FORM_FIELD_KEY.COUNTRY) {
    const zip_field: CheckoutFormField = findOrThrow(billing_info_fields, {
      key: "zip"
    });
    const zip_state = billing_info_state.zip;
    const formatted_zip = formatter.zip(value, zip_state?.value || "");
    const region_code =
      geoData.getRegionCodeFromPostalCode(value, formatted_zip) || "";

    // ID type, ID number, email and phone are dependent on country.
    // Update their states when country changes so they are validated/cleared properly.
    const id_type_field: CheckoutFormField = findOrThrow(billing_info_fields, {
      key: "id_type"
    });
    const id_number_field: CheckoutFormField = findOrThrow(
      billing_info_fields,
      {
        key: "id_number"
      }
    );
    const email_field: CheckoutFormField = findOrThrow(billing_info_fields, {
      key: "email"
    });
    const phone_number_field: CheckoutFormField = findOrThrow(
      billing_info_fields,
      {
        key: "phone_number"
      }
    );

    const id_type_value = geoData.getBillingIDType(value) || "";
    const id_number_value = geoData.getExtraBillingField(value, "id")
      ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.id_number.value
      : "";
    const email_value = geoData.getExtraBillingField(value, "email")
      ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.email.value
      : "";
    const phone_number_value = geoData.getExtraBillingField(
      value,
      "phone_number"
    )
      ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.phone_number.value
      : "";

    return Object.assign({}, billing_info_state, {
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      country: updateInputState(field, input_state, value),
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      state: updateInputState(state_field, state_state, region_code), // Select state/province based on `zip` value
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      zip: updateInputState(zip_field, zip_state, formatted_zip), // Trigger `zip` input validation and formatting
      id_type: updateInputState(
        id_type_field,
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.id_type,
        id_type_value
      ),
      id_number: updateInputState(
        id_number_field,
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.id_number,
        id_number_value
      ),
      email: updateInputState(
        email_field,
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.email,
        email_value
      ),
      phone_number: updateInputState(
        phone_number_field,
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.phone_number,
        phone_number_value
      )
    });
  }

  if (field.key === FORM_FIELD_KEY.ZIP) {
    const region_code =
      geoData.getRegionCodeFromPostalCode(
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        billing_info_state.country.value,
        value
      ) || "";

    return Object.assign({}, billing_info_state, {
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      state: updateInputState(state_field, state_state, region_code),
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      zip: updateInputState(field, input_state, value)
    });
  }

  return Object.assign({}, billing_info_state, {
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    [field.key]: updateInputState(field, input_state, value)
  });
}

export function getUpdatedBillingInfo(
  billing_info_state: Record<string, CheckoutInputState>
): BillingInfo {
  const billing_info: Partial<BillingInfo> = {};

  Object.keys(billing_info_state).forEach(key => {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    billing_info[key as keyof BillingInfo] = billing_info_state[key].value;
  });

  return billing_info as BillingInfo;
}

export function shouldShowInputError(
  is_form_submitted: boolean,
  input_state: CheckoutInputState
): boolean {
  return !!(
    (is_form_submitted && input_state.error) ||
    (input_state.blurred && input_state.changed && input_state.error)
  );
}

export function makeQuestionOptionsFilter(
  cart: PassengerCartInfo["value"],
  field_key: CheckoutFormFieldKey
): (option: { value: string; label: string }) => boolean {
  const choices =
    cart.questions &&
    cart.questions[field_key] &&
    cart.questions[field_key].choices;

  return choices
    ? option => choices.indexOf(option.value as any) > -1
    : () => true;
}

/**
 * Fix for chrome autofill bug. If a form field has been visited,
 * set autocomplete to off so chrome doesn't attempt to overwrite it with autofill
 *
 * @param input_state
 * @param field
 * @returns
 */
export function getAutocompleteAttribute(
  input_state: CheckoutInputState,
  field: CheckoutFormField
): string | undefined {
  return !input_state.error && input_state.blurred ? "off" : field.autocomplete;
}

export function createPrimaryContactInfoState(
  primary_contact_info_fields: Array<CheckoutFormField>,
  user: NapiUser | undefined | null,
  restored_primary_contact_info: PrimaryContactInfo | undefined | null,
  email_opt_in_default_value: boolean
): CheckoutPrimaryContactInfoState {
  return primary_contact_info_fields.reduce<CheckoutPrimaryContactInfoState>(
    (primary_contact_info_state, field) => {
      let initial_value = restored_primary_contact_info
        ? restored_primary_contact_info[field.key as keyof PrimaryContactInfo]
        : null;

      if (field.key === FORM_FIELD_KEY.EMAIL && user) {
        initial_value = user.email;
      }

      if (field.key === FORM_FIELD_KEY.EMAIL_OPT_IN) {
        initial_value = email_opt_in_default_value.toString();
      }

      primary_contact_info_state[
        field.key as keyof CheckoutPrimaryContactInfoState
      ] = getInitialInputState(field, initial_value);
      return primary_contact_info_state;
    },
    {} as CheckoutPrimaryContactInfoState
  );
}

export function updatePrimaryContactInfoState(
  _primary_contact_info_fields: Array<CheckoutFormField>,
  primary_contact_info_state: CheckoutPrimaryContactInfoState,
  field: CheckoutFormField,
  value: string
): CheckoutPrimaryContactInfoState {
  const input_state: CheckoutInputState =
    primary_contact_info_state[
      field.key as keyof CheckoutPrimaryContactInfoState
    ];

  return Object.assign({}, primary_contact_info_state, {
    [field.key]: updateInputState(field, input_state, value)
  });
}

/**
 * Check if all or a given passenger's seats are selected.
 *
 * @param seat_selection_state_value
 * @param departure_id
 * @param passenger_index
 */
function _seatsAreSelected(
  seat_selection_state_value: SeatSelectionStateValue,
  departure_id: string,
  passenger_index: number | undefined
) {
  const departure_seat_selection = seat_selection_state_value[departure_id];
  return passenger_index !== undefined
    ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      departure_seat_selection.seats[passenger_index] !== null
    : // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      !departure_seat_selection.seats.some(seat => seat === null);
}

/**
 * Check that the whole seat selection is valid.
 * It can be invalid if it's mandatory (has gender seats) and at least one seat is not selected.
 *
 * @param departures
 * @param seat_selection_state_value
 * @param passenger_index
 */
export function seatSelectionValid(
  departures: NapiDepartureCart[],
  seat_selection_state_value: SeatSelectionStateValue,
  passenger_index?: number
): boolean {
  return (
    !seatSelectionHasGenders(departures) ||
    departures.every(
      departure =>
        !departure.vehicle ||
        (seat_selection_state_value[departure.id] &&
          _seatsAreSelected(
            seat_selection_state_value,
            departure.id,
            passenger_index
          ))
    )
  );
}
